import React, { useState, useCallback, useEffect, useRef } from "react";
import Pagination from "@material-ui/lab/Pagination";
import { makeStyles } from "@material-ui/core/styles";
import { Breadcrumbs } from "@material-ui/core";
import NavigateNextIcon from "@material-ui/icons/NavigateNext";
import { useDispatch, useSelector } from "react-redux";
import queryString from "query-string";
import theme from "../../theme";
import CommonButton from "../button";
import MerchantList from "./list";
import MerchantDetail from "./detail";
import InviteMerchant from "./inviteMerchant";
import {
  getMerchantListAction,
  getMerchantInfoAction,
  inviteMerchantAction,
  getVendorPaymentsAction,
  updateMerchantAction,
  listVendorBankDetailsAction,
  addVendorBankDetailsAction,
  updateVendorBankDetailsAction,
  deleteVendorBankDetailsAction,
  getVendorBankDetailsAction,
  vendorResendInvitationAction,
  vendorSettlementTriggerAction
} from "../../containers/dashboard/action";
import { ThreeDotsLoading } from "../loading";
import history from "../../history";
import { hasCap, isSuper } from "../../lib/permissions";

const useStyles = makeStyles(() => ({
  root: {
    backgroundColor: theme.colors.White,
    width: "100%",
    minHeight: "calc(100vh - 175px)",
    padding: "45px 90px",
    marginLeft: "220px",
  },
  headerContainer: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    "& .title": {
      color: theme.colors.Violet,
      fontFamily: theme.fontFamily.MARKPROMEDIUM,
      fontSize: "24px",
      "& .deactiveLabel": { color: "#9FC1FD", cursor: "pointer" },
      "& .activeLabel": {
        color: theme.colors.DodgerBlue,
        cursor: "pointer",
      },
    },
  },
  backLink: {
    marginTop: "15px",
    color: theme.colors.DodgerBlueLight,
    fontSize: "16px",
    fontFamily: theme.fontFamily.MARKPROMEDIUM,
    cursor: "pointer",
    alignItems: "center",
    display: "flex",
    "& .label": { marginLeft: "5px", position: "relative", top: 2 },
  },
}));

function Merchants({ location }) {
  const classes = useStyles();
  const dispatch = useDispatch();

  const [page, setPage] = useState(1);
  const [totalPage, setTotalPage] = useState(1);
  const page_size = 10;
  const prevReactivateSuccessRef = useRef(null);
  const prevSuspendSuccessRef = useRef(null);
  const prevUpdateMerchantSuccessRef = useRef(null);

  const getMerchantListMethod = useCallback(
    (data) => dispatch(getMerchantListAction(data)),
    [dispatch]
  );

  const getMerchantInfoMethod = useCallback(
    (data) => dispatch(getMerchantInfoAction(data)),
    [dispatch]
  );

  const getVendorPaymentsMethod = useCallback(
    (vendor_id, data) => dispatch(getVendorPaymentsAction(vendor_id, data)),
    [dispatch]
  );

  const updateMerchantMethod = useCallback(
    (data, merchant_id) => dispatch(updateMerchantAction(data, merchant_id)),
    [dispatch]
  );

  const inviteMerchantMethod = useCallback(
    (data) => dispatch(inviteMerchantAction(data)),
    [dispatch]
  );

  const listVendorBankDetailsMethod = useCallback(
    (data) => dispatch(listVendorBankDetailsAction(data)),
    [dispatch]
  );

  const addVendorBankDetailsMethod = useCallback(
    (data) => dispatch(addVendorBankDetailsAction(data)),
    [dispatch]
  );

  const updateVendorBankDetailsMethod = useCallback(
    (data) => dispatch(updateVendorBankDetailsAction(data)),
    [dispatch]
  );

  const deleteVendorBankDetailsMethod = useCallback(
    (data) => dispatch(deleteVendorBankDetailsAction(data)),
    [dispatch]
  );

  const getVendorBankDetailsMethod = useCallback(
    (data) => dispatch(getVendorBankDetailsAction(data)),
    [dispatch]
  );

  const vendorSettlementTriggerMethod = useCallback(
    data => dispatch(vendorSettlementTriggerAction(data)),
    [dispatch]
  );

  const vendorResendInvitationMethod = useCallback(
    data => dispatch(vendorResendInvitationAction(data)),
    [dispatch]
  )

  const dashboardReducer = useSelector((state) => {
    return state.dashboardReducer;
  });

  // Vendor mutations gate on vendors.edit; bank accounts / fees stay super-only.
  const me = useSelector((state) => state?.loginReducer?.me);
  let storedUser = null;
  try {
    storedUser = JSON.parse(localStorage.getItem("user"));
  } catch (e) {
    storedUser = null;
  }
  const currentUser = me || storedUser;
  const canEditVendors = hasCap(currentUser, "vendors.edit");
  const superUser = isSuper(currentUser);

  const [merchantDetail, setMerchantDetail] = useState(null);
  const [inviteMerchant, setInviteMerchant] = useState(false);

  useEffect(() => {
    if (location?.search) {
      const parsed = queryString.parse(location.search);

      if (parsed?.merchantId) {
        if (
          dashboardReducer?.merchantList?.data?.length &&
          (!merchantDetail?.id || merchantDetail.id !== parsed.merchantId)
        ) {
          const record = dashboardReducer?.merchantList?.data.find(
            (each) => each?.id === parsed?.merchantId
          );
          if (record) {
            setMerchantDetail(record);
            getMerchantInfoMethod(record.id);
          } else {
            setMerchantDetail(null);
          }
        }
      } else {
        setMerchantDetail(null);
      }
    } else {
      setMerchantDetail(null);
    }
  }, [location, merchantDetail, dashboardReducer, getMerchantInfoMethod]);

  useEffect(() => {
    getMerchantListMethod({ page, page_size });
  }, [getMerchantListMethod, page, page_size]);

  useEffect(() => {
    if (dashboardReducer?.success) {
      getMerchantListMethod({ page, page_size });
      setInviteMerchant(false);
      if (merchantDetail?.id) {
        getMerchantInfoMethod(merchantDetail.id);
      }
    }
  }, [
    dashboardReducer?.success,
    getMerchantListMethod,
    merchantDetail,
    getMerchantInfoMethod,
    page,
    page_size,
  ]);

  useEffect(() => {
    const currentUpdateMerchantSuccess = dashboardReducer?.updateMerchantSuccess;
    if (
      currentUpdateMerchantSuccess &&
      currentUpdateMerchantSuccess !== prevUpdateMerchantSuccessRef.current
    ) {
      prevUpdateMerchantSuccessRef.current = currentUpdateMerchantSuccess;
      getMerchantListMethod({ page, page_size });
      setInviteMerchant(false);
      if (merchantDetail?.id) {
        getMerchantInfoMethod(merchantDetail.id);
      }
    }
  }, [
    dashboardReducer?.updateMerchantSuccess,
    getMerchantListMethod,
    merchantDetail,
    getMerchantInfoMethod,
    page,
    page_size,
  ]);

  useEffect(() => {
    const currentReactivateSuccess = dashboardReducer?.reactivateVendorSuccess;
    if (
      currentReactivateSuccess &&
      currentReactivateSuccess !== prevReactivateSuccessRef.current
    ) {
      prevReactivateSuccessRef.current = currentReactivateSuccess;
      getMerchantListMethod({ page, page_size });
      setInviteMerchant(false);
      if (merchantDetail?.id) {
        getMerchantInfoMethod(merchantDetail.id);
      }
    }
  }, [
    dashboardReducer?.reactivateVendorSuccess,
    getMerchantListMethod,
    merchantDetail,
    getMerchantInfoMethod,
    page,
    page_size,
  ]);

  useEffect(() => {
    const currentSuspendSuccess = dashboardReducer?.suspendVendorSuccess;
    if (
      currentSuspendSuccess &&
      currentSuspendSuccess !== prevSuspendSuccessRef.current
    ) {
      prevSuspendSuccessRef.current = currentSuspendSuccess;
      getMerchantListMethod({ page, page_size });
      setInviteMerchant(false);
      if (merchantDetail?.id) {
        getMerchantInfoMethod(merchantDetail.id);
      }
    }
  }, [
    dashboardReducer?.suspendVendorSuccess,
    getMerchantListMethod,
    merchantDetail,
    getMerchantInfoMethod,
    page,
    page_size,
  ]);

  useEffect(() => {
    if (dashboardReducer?.merchantList?.count) {
      setTotalPage(Math.ceil(dashboardReducer.merchantList.count / page_size));
    }
  }, [dashboardReducer]);

  useEffect(() => {
    if (dashboardReducer?.error?.length) {
      const ele = document.getElementsByClassName("error-container");
      if (ele && ele.length) ele[0].scrollIntoView(false);
    }
  }, [dashboardReducer]);

  return (
    <div className={classes.root}>
      <ThreeDotsLoading showLoader={
        dashboardReducer?.loading ||
        dashboardReducer?.listVendorBankDetailsLoading ||
        dashboardReducer?.addVendorBankDetailsLoading ||
        dashboardReducer?.updateVendorBankDetailsLoading ||
        dashboardReducer?.vendorResendInvitationLoading
      } />
      <div className={classes.headerContainer}>
        <div className="title">
          <div>Merchants</div>
          {merchantDetail ? (
            <div>
              <Breadcrumbs
                separator={
                  <NavigateNextIcon
                    fontSize="small"
                    style={{ color: "#9FC1FD" }}
                  />
                }
              >
                <div
                  className="deactiveLabel"
                  onClick={() => {
                    setInviteMerchant(false);
                    history.push("/backoffice/dashboard/merchants");
                  }}
                >
                  Merchants
                </div>
                <div className="activeLabel">{merchantDetail?.business_name}</div>
              </Breadcrumbs>
            </div>
          ) : null}
        </div>
        {canEditVendors && !(merchantDetail || inviteMerchant) ? (
          <div>
            <CommonButton
              type="button"
              text="Invite New Merchant"
              stylevariant="primary"
              onClick={() => {
                setInviteMerchant(true);
              }}
              style={{ padding: "5px 40px" }}
              fontSize="18px"
              fontFamily={theme.fontFamily.MARKPROHEAVY}
            />
          </div>
        ) : null}
      </div>
      {inviteMerchant ? (
        <div
          className={classes.backLink}
          onClick={() => {
            setInviteMerchant(false);
          }}
        >
          <img src="/images/right-arrow.png" alt="right-arrow" />
          <span className="label">Back</span>
        </div>
      ) : null}
      {dashboardReducer?.error?.length ||
        dashboardReducer?.inviteMerchantError?.length ||
        dashboardReducer?.reactivateVendorError?.length ||
        dashboardReducer?.suspendVendorError?.length ? (
        <div className={"error-container"}>
          {dashboardReducer?.error?.length
            ? dashboardReducer.error.map((each, index) => (
              <div key={index}>{each}</div>
            ))
            : null}
          {dashboardReducer?.inviteMerchantError?.length
            ? dashboardReducer.inviteMerchantError.map((each, index) => (
              <div key={index}>{each}</div>
            ))
            : null}
          {dashboardReducer?.reactivateVendorError?.length
            ? dashboardReducer.reactivateVendorError.map((each, index) => (
              <div key={index}>{each}</div>
            ))
            : null}
          {dashboardReducer?.suspendVendorError?.length
            ? dashboardReducer.suspendVendorError.map((each, index) => (
              <div key={index}>{each}</div>
            ))
            : null}
        </div>
      ) : null}
      {inviteMerchant ? (
        <InviteMerchant
          inviteMerchantMethod={inviteMerchantMethod}
          dashboardReducer={dashboardReducer}
        />
      ) : merchantDetail ? (
        <MerchantDetail
          canEditVendors={canEditVendors}
          superUser={superUser}
          merchantInfo={dashboardReducer?.merchantInfo || merchantDetail}
          getVendorPaymentsMethod={getVendorPaymentsMethod}
          updateMerchantMethod={updateMerchantMethod}
          getMerchantInfoMethod={getMerchantInfoMethod}
          listVendorBankDetailsMethod={listVendorBankDetailsMethod}
          addVendorBankDetailsMethod={addVendorBankDetailsMethod}
          updateVendorBankDetailsMethod={updateVendorBankDetailsMethod}
          deleteVendorBankDetailsMethod={deleteVendorBankDetailsMethod}
          getVendorBankDetailsMethod={getVendorBankDetailsMethod}
          vendorSettlementTriggerMethod={vendorSettlementTriggerMethod}
        />
      ) : (
        <>
          <MerchantList
            canEditVendors={canEditVendors}
            data={dashboardReducer?.merchantList?.data}
            onMerchantClick={(merchant) => {
              history.push(
                `/backoffice/dashboard/merchants?merchantId=${merchant?.id}`
              );
            }}
            onResendInvitationMethod={vendorResendInvitationMethod}
          />
          {dashboardReducer?.merchantList?.data?.length ? (
            <div className={"pagination"}>
              <Pagination
                page={page}
                count={totalPage}
                showFirstButton
                showLastButton
                onChange={(event, value) => {
                  setPage(value);
                }}
              />
            </div>
          ) : null}
        </>
      )}
    </div>
  );
}

export default Merchants;

